import React from 'react';
import { Box, Text } from '@primer/react';
import { Link } from 'react-router-dom';
import { HomeIcon, GitMergeIcon, CodeIcon, RocketIcon, ListUnorderedIcon } from '@primer/octicons-react';

const Header = ({ activeSection }) => {
    return (
        <Box as="header" mb={4}>
            <Box mb={3}>
                <Text
                    as="h1"
                    sx={{
                        fontSize: [5, 6, 7],
                        fontWeight: 'bold',
                        letterSpacing: '-0.05em',
                        color: 'fg.default'
                    }}
                >
                    Hey I'm Dylan.
                </Text>
            </Box>

            <Box mb={5}>
                <Box
                    sx={{
                        fontSize: [1, 2],
                        color: 'fg.muted',
                        lineHeight: 1.2,
                        maxWidth: '55rem',
                        letterSpacing: '0.01em',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1
                    }}
                >
                    <Text>Senior Software Engineer @ Dialogue</Text>
                    <Text>5+ years of experience in Montréal, San Francisco, London, and in France (Paris, Toulouse, Lyon)</Text>
                    <Text>Specialized in building resilient, high-performance systems that scale</Text>
                </Box>
            </Box>

            <Box
                display="flex"
                flexDirection={["column", "row"]}
                mb={4}
                gap={[2, 4, 6]}
                className="header-nav"
                sx={{
                    width: '100%',
                    px: [0, 0, 0] // Remove any horizontal padding on mobile
                }}
            >
                <Box
                    as={Link}
                    to="/"
                    bg={activeSection === 'intro' ? 'accent.subtle' : 'transparent'}
                    px={3}
                    py={2}
                    mr={[0, 3]}
                    mb={[2, 0]}
                    className="nav-link"
                    sx={{
                        border: '1px solid',
                        borderColor: activeSection === 'intro' ? 'accent.muted' : 'border.default',
                        borderRadius: 2,
                        fontWeight: 500,
                        textDecoration: 'none',
                        color: 'inherit',
                        '&:hover': {
                            bg: 'accent.subtle',
                            borderColor: 'accent.muted',
                            textDecoration: 'none'
                        }
                    }}
                >
                    <Box display="flex" alignItems="center">
                        <Box mr={2}><HomeIcon size={16} /></Box>
                        <Text>About Me</Text>
                    </Box>
                </Box>

                <Box
                    as={Link}
                    to="/timeline"
                    bg={activeSection === 'timeline' ? 'accent.subtle' : 'transparent'}
                    px={3}
                    py={2}
                    mr={[0, 3]}
                    mb={[2, 0]}
                    className="nav-link"
                    sx={{
                        border: '1px solid',
                        borderColor: activeSection === 'timeline' ? 'accent.muted' : 'border.default',
                        borderRadius: 2,
                        fontWeight: 500,
                        textDecoration: 'none',
                        color: 'inherit',
                        '&:hover': {
                            bg: 'accent.subtle',
                            borderColor: 'accent.muted',
                            textDecoration: 'none'
                        }
                    }}
                >
                    <Box display="flex" alignItems="center">
                        <Box mr={2}><GitMergeIcon size={16} /></Box>
                        <Text>Experience</Text>
                    </Box>
                </Box>

                <Box
                    as={Link}
                    to="/projects"
                    bg={activeSection === 'projects' ? 'attention.subtle' : 'transparent'}
                    px={3}
                    py={2}
                    mr={[0, 0]}
                    mb={[2, 0]}
                    className="nav-link"
                    sx={{
                        border: '1px solid',
                        borderColor: activeSection === 'projects' ? 'attention.muted' : 'border.default',
                        borderRadius: 2,
                        fontWeight: 500,
                        textDecoration: 'none',
                        color: 'inherit',
                        '&:hover': {
                            bg: 'attention.subtle',
                            borderColor: 'attention.muted',
                            textDecoration: 'none'
                        }
                    }}
                >
                    <Box display="flex" alignItems="center">
                        <Box mr={2}><CodeIcon size={16} /></Box>
                        <Text>Projects</Text>
                    </Box>
                </Box>

                {/* Delimiter - Separates personal portfolio from DSA project */}
                <Box
                    sx={{
                        width: ['100%', '1px'],
                        height: ['1px', '40px'],
                        bg: 'border.muted',
                        alignSelf: 'center',
                        mx: [0, 3],
                        my: [3, 0]
                    }}
                />

                <Box
                    as={Link}
                    to="/learn-dsa"
                    bg={activeSection === 'learn-dsa' ? 'open.subtle' : 'transparent'}
                    px={3}
                    py={2}
                    mr={[0, 3]}
                    mb={[2, 0]}
                    className="nav-link"
                    sx={{
                        border: '1px solid',
                        borderColor: activeSection === 'learn-dsa' ? 'open.muted' : 'border.default',
                        borderRadius: 2,
                        fontWeight: 500,
                        textDecoration: 'none',
                        color: 'inherit',
                        '&:hover': {
                            bg: 'open.subtle',
                            borderColor: 'open.muted',
                            textDecoration: 'none'
                        }
                    }}
                >
                    <Box display="flex" alignItems="center">
                        <Box mr={2}><RocketIcon size={16} /></Box>
                        <Text>Learn DSA</Text>
                    </Box>
                </Box>

                {/* Second Delimiter - Separates DSA content from meta/website content */}
                <Box
                    sx={{
                        width: ['100%', '1px'],
                        height: ['1px', '30px'],
                        bg: 'border.muted',
                        alignSelf: 'center',
                        mx: [0, 2],
                        my: [2, 0]
                    }}
                />

                <Box
                    as={Link}
                    to="/changelog"
                    bg={activeSection === 'changelog' ? 'neutral.subtle' : 'transparent'}
                    px={2}
                    py={1}
                    ml={3}
                    className="nav-link"
                    sx={{
                        border: '1px solid',
                        borderColor: activeSection === 'changelog' ? 'neutral.muted' : 'border.muted',
                        borderRadius: 1,
                        fontWeight: 400,
                        textDecoration: 'none',
                        color: 'fg.muted',
                        fontSize: 0,
                        opacity: 0.8,
                        alignSelf: 'center',
                        display: 'flex',
                        alignItems: 'center',
                        '&:hover': {
                            bg: 'neutral.subtle',
                            borderColor: 'neutral.muted',
                            textDecoration: 'none',
                            opacity: 1,
                            color: 'inherit'
                        }
                    }}
                >
                    <Box display="flex" alignItems="center">
                        <Box mr={1} display="flex" alignItems="center"><ListUnorderedIcon size={12} /></Box>
                        <Text fontSize={0}>Changelog</Text>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default Header;